import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import { createApplication } from '../../services/applications/applicationsService';
import { ApplicationDto, ApplicationStatus, ApplicationVendor } from '../../services/applications/types';

const ApplicationCreate: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [formData, setFormData] = useState<ApplicationDto>({
    name: '',
    identifier: '',
    status: ApplicationStatus.ACTIVATED,
    vendor: ApplicationVendor.WEB
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to create an application.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const newApplication = await createApplication(auth.user || null, formData);
      navigate(`/applications/${newApplication.id}`);
    } catch (err) {
      setError('Failed to create application. Please try again.');
      console.error('Error creating application:', err);
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/applications');
  };
  
  return (
    <div>
      <PageHeader
        title="Create Application"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: "Create Application" }
        ]}
      />
      
      <Card>
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-4">
                <FormInput
                  label="Name"
                  id="name"
                  name="name"
                  required
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  placeholder="Enter application name"
                />

                <FormInput
                  label="Identifier"
                  id="identifier"
                  name="identifier"
                  required
                  value={formData.identifier || ''}
                  onChange={handleInputChange}
                  placeholder="Enter unique identifier"
                  helperText="Unique identifier for this application. Cannot be changed later."
                />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormSelect
                  label="Vendor"
                  id="vendor"
                  name="vendor"
                  required
                  value={formData.vendor || ''}
                  onChange={handleInputChange}
                  options={Object.entries(ApplicationVendor).map(([key, value]) => ({
                    value: value,
                    label: key
                  }))}
                />

                <FormSelect
                  label="Status"
                  id="status"
                  name="status"
                  required
                  value={formData.status || ''}
                  onChange={handleInputChange}
                  options={Object.entries(ApplicationStatus).map(([key, value]) => ({
                    value: value,
                    label: key
                  }))}
                />
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Application'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ApplicationCreate;